import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  useTheme,
  Tooltip,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useAppBar } from '../../providers/AppBarProvider';
import axios from 'axios';
import { handleError } from '../../utils/lib';
import { useTranslation } from 'react-i18next';
import VetActionsBottomSheet from '../../layouts/VetActionsBottomSheet';

const ViewVetPoints = () => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle } = useAppBar();
  const [points, setPoints] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    updateTitle('', t('view.emissionPointsTitle'), t('view.emissionPointsSubtitle'));
    fetchPoints();
  }, [t]);

  const fetchPoints = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}vets?all_points`,
        { withCredentials: true }
      );
      setPoints(response.data.data || []);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (item) => {
    setIdToDelete(item.id);
    setDeleteDialogOpen(true);
  };

  const handleCreate = () => {
    navigate('/main/edit-vet-point');
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${import.meta.env.VITE_API_URL}vets?delete_point&id=${idToDelete}`,
        { withCredentials: true }
      );
      setPoints(points.filter(s => s.id !== idToDelete));
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setIdToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Paper sx={{ p: 2 }}>
        <List>
          {points.map((point, index) => (
            <React.Fragment key={point.id}>
              <ListItem
                secondaryAction={
                  <Tooltip title={t('view.deleteEmissionPoint')}>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDelete(point)}
                      color="default"
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                }
                sx={{ paddingRight: "96px" }}
              >
                <ListItemText
                  primary={point.name}
                  secondary={t('view.emissionPointListLine', {
                    number: point.number,
                    type: point.type,
                  })}
                />
              </ListItem>
              {index < points.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('common.confirmDeletion')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('view.confirmDeleteEmissionPoint')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus disabled={loading}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            startIcon={<AddIcon />}
          >
            {t('view.createEmissionPoint')}
          </Button>
        </Stack>
      </Box>

      <VetActionsBottomSheet />
    </Container>
  );
};

export default ViewVetPoints; 